import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import {
  Loader2,
  ShieldAlert,
  Search,
  AlertTriangle,
  CheckCircle2,
  Undo2,
  Plus,
  Trash2,
  MinusCircle,
} from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface LookupResult {
  account: string;
  customerid: string;
  accountFound: boolean;
  invoiceRef: string | null;
  found: boolean;
  ambiguous: boolean;
  sltranid: string | null;
  invoiceTotal: number | null;
  priorPaid: number | null;
  balance: number | null;
}

// Per-line preview row produced by looking each invoice up in Merlin.
interface LineLookup {
  invoiceRef: string;
  unallocated?: boolean;
  found?: boolean;
  ambiguous?: boolean;
  sltranid?: string | null;
  invoiceTotal?: number | null;
  priorPaid?: number | null;
  balance?: number | null;
  error?: string;
}

interface BatchPreview {
  account: string;
  customerid: string;
  lines: LineLookup[];
}

interface BatchLineResult {
  index: number;
  invoiceRef: string | null;
  amount: number;
  sltranid: string | null;
  posted: boolean;
  code: string | null;
  message: string | null;
  error: string | null;
}

interface BatchPostResult {
  ok: boolean;
  account: string;
  customerid: string;
  reference: string;
  lines: BatchLineResult[];
  allPosted: boolean;
  postedCount: number;
  totalAmount: number;
}

interface LineInput {
  invoiceRef: string;
  amount: string;
}

const gbp = (n: number | null | undefined) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n);

// Per-batch idempotency key (crypto.randomUUID with a fallback for any
// non-secure context) so a retry/replay can't double-post to the live ledger.
const makeIdempotencyKey = (): string => {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `adj-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export function ManualAdjustmentForm() {
  const { toast } = useToast();
  const [account, setAccount] = useState("");
  const [reference, setReference] = useState("");
  const [lines, setLines] = useState<LineInput[]>([{ invoiceRef: "", amount: "" }]);
  const [preview, setPreview] = useState<BatchPreview | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [result, setResult] = useState<BatchPostResult | null>(null);
  const [confirming, setConfirming] = useState(false);
  // Stable idempotency key for the composed batch. Generated when the operator
  // opens the confirm step, reused across retries of the SAME batch (so a
  // network-replay returns the original ledger result instead of double-posting)
  // and cleared when inputs change or a post succeeds.
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);

  // Any field change invalidates a prior lookup/preview so the operator can't
  // post against stale resolution, and starts a fresh idempotency key.
  const resetPreview = () => {
    setPreview(null);
    setLookupError(null);
    setResult(null);
    setConfirming(false);
    setIdempotencyKey(null);
  };

  const updateLine = (i: number, patch: Partial<LineInput>) => {
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
    resetPreview();
  };
  const addLine = () => {
    setLines((prev) => [...prev, { invoiceRef: "", amount: "" }]);
    resetPreview();
  };
  const removeLine = (i: number) => {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== i)));
    resetPreview();
  };

  // Drop fully-empty rows, then parse. A row counts once it has either field.
  const parsedLines = lines
    .map((l) => ({
      invoiceRef: l.invoiceRef.trim(),
      amountStr: l.amount.trim(),
      amount: Number(l.amount),
    }))
    .filter((l) => !(l.invoiceRef === "" && l.amountStr === ""));

  const linesValid =
    parsedLines.length > 0 &&
    parsedLines.every(
      (l) => l.amountStr !== "" && Number.isFinite(l.amount) && l.amount !== 0,
    );
  const canSubmit = account.trim() !== "" && reference.trim() !== "" && linesValid;

  const totalAmount = parsedLines.reduce(
    (sum, l) => sum + (Number.isFinite(l.amount) ? l.amount : 0),
    0,
  );
  const hasReversal = parsedLines.some((l) => Number.isFinite(l.amount) && l.amount < 0);

  const lookupMutation = useMutation({
    mutationFn: async (): Promise<BatchPreview> => {
      // Validate the account once (also gives us the customer id).
      const acctParams = new URLSearchParams({ account: account.trim() });
      const acctRes = await fetch(
        `${BASE}/api/cash-receipts/manual-adjustment/lookup?${acctParams.toString()}`,
        { credentials: "include" },
      );
      const acctBody = (await acctRes.json().catch(() => ({}))) as Partial<LookupResult> & {
        error?: string;
      };
      if (!acctRes.ok) throw new Error(acctBody?.error || "Account lookup failed");

      // Resolve each invoice line (skip unallocated rows).
      const lineResults: LineLookup[] = await Promise.all(
        parsedLines.map(async (l): Promise<LineLookup> => {
          if (!l.invoiceRef) return { invoiceRef: "", unallocated: true };
          const p = new URLSearchParams({ account: account.trim(), invoice: l.invoiceRef });
          const r = await fetch(
            `${BASE}/api/cash-receipts/manual-adjustment/lookup?${p.toString()}`,
            { credentials: "include" },
          );
          const b = (await r.json().catch(() => ({}))) as Partial<LookupResult> & {
            error?: string;
          };
          if (!r.ok) return { invoiceRef: l.invoiceRef, error: b?.error || "Lookup failed" };
          return {
            invoiceRef: l.invoiceRef,
            found: b.found,
            ambiguous: b.ambiguous,
            sltranid: b.sltranid,
            invoiceTotal: b.invoiceTotal,
            priorPaid: b.priorPaid,
            balance: b.balance,
          };
        }),
      );

      return {
        account: acctBody.account || account.trim(),
        customerid: acctBody.customerid || "",
        lines: lineResults,
      };
    },
    onSuccess: (data) => {
      setPreview(data);
      setLookupError(null);
    },
    onError: (err: any) => {
      setPreview(null);
      setLookupError(err?.message || "Lookup failed");
    },
  });

  const postMutation = useMutation({
    mutationFn: async (): Promise<BatchPostResult> => {
      const res = await fetch(`${BASE}/api/cash-receipts/manual-adjustment/batch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          account: account.trim(),
          reference: reference.trim(),
          lines: parsedLines.map((l) => ({
            invoiceRef: l.invoiceRef || null,
            amount: l.amount,
          })),
          idempotencyKey,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || body?.message || "Posting failed");
      return body as BatchPostResult;
    },
    onSuccess: (data) => {
      setResult(data);
      setConfirming(false);
      // Posted (fully or partially) — retire this key so the next batch composes
      // a fresh one (a repeat with this key would just replay this result).
      setIdempotencyKey(null);
      if (data.allPosted) {
        toast({
          title: "Posted to Merlin",
          description: `${data.postedCount} line${data.postedCount === 1 ? "" : "s"} → ${data.account} · ${gbp(data.totalAmount)}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Batch stopped",
          description: `${data.postedCount} of ${data.lines.length} line${data.lines.length === 1 ? "" : "s"} posted before a failure — see breakdown.`,
        });
      }
    },
    onError: (err: any) => {
      setConfirming(false);
      toast({
        variant: "destructive",
        title: "Posting failed",
        description: err?.message || "Merlin rejected the batch",
      });
    },
  });

  const lineCount = parsedLines.length;

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Undo2 className="w-6 h-6 text-primary" />
          Manual Cash-Receipt Adjustment
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Post one or more CashReceipts directly to Merlin against a single account — used
          to undo a posting error (enter a <strong>negative</strong> amount to reverse) or
          book cash against one or several invoices (or on-account).
        </p>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-6 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <p className="font-semibold">This writes straight to the live Merlin ledger.</p>
          <p className="mt-1">
            There are no reconciliation or duplicate checks beyond what Merlin itself
            enforces. Lines post in order and the batch stops at the first failure —
            earlier lines stay posted. Every line is recorded in the audit log. Double-check
            the account, amounts and reference before confirming.
          </p>
        </div>
      </div>

      <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Merlin account <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={account}
            onChange={(e) => {
              setAccount(e.target.value.toUpperCase());
              resetPreview();
            }}
            placeholder="e.g. WAY002"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none uppercase"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Reference <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={reference}
            maxLength={100}
            onChange={(e) => {
              setReference(e.target.value);
              resetPreview();
            }}
            placeholder="Shown in Merlin on every line — e.g. 'Reallocation: BP373'"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-slate-700">
              Invoice lines <span className="text-red-500">*</span>
            </label>
            <span className="text-xs text-slate-400">
              Leave the invoice blank to post that line unallocated (on-account).
            </span>
          </div>

          <div className="space-y-2">
            {lines.map((line, i) => {
              const amtNum = Number(line.amount);
              const amtInvalid =
                line.amount.trim() !== "" &&
                (!Number.isFinite(amtNum) || amtNum === 0);
              return (
                <div key={i} className="flex items-start gap-2">
                  <input
                    type="text"
                    value={line.invoiceRef}
                    onChange={(e) => updateLine(i, { invoiceRef: e.target.value })}
                    placeholder="Invoice ref (optional)"
                    className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                  />
                  <div className="w-36">
                    <input
                      type="number"
                      step="0.01"
                      value={line.amount}
                      onChange={(e) => updateLine(i, { amount: e.target.value })}
                      placeholder="£ amount"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                    />
                    {amtInvalid && (
                      <p className="text-xs text-red-500 mt-1">Non-zero number.</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeLine(i)}
                    disabled={lines.length <= 1}
                    title="Remove line"
                    className="mt-1.5 text-slate-400 hover:text-red-500 disabled:opacity-30 disabled:hover:text-slate-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mt-2 flex items-center justify-between">
            <Button type="button" variant="outline" size="sm" onClick={addLine}>
              <Plus className="w-4 h-4 mr-1" /> Add line
            </Button>
            {lineCount > 0 && (
              <p className="text-sm text-slate-600">
                {lineCount} line{lineCount === 1 ? "" : "s"} · total{" "}
                <strong className={totalAmount < 0 ? "text-amber-700" : "text-slate-900"}>
                  {gbp(totalAmount)}
                </strong>
              </p>
            )}
          </div>

          {hasReversal && (
            <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
              <MinusCircle className="w-3 h-3" /> A negative amount reverses a prior receipt.
            </p>
          )}
        </div>

        <div className="flex items-center gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => lookupMutation.mutate()}
            disabled={account.trim() === "" || !linesValid || lookupMutation.isPending}
          >
            {lookupMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Search className="w-4 h-4 mr-2" />
            )}
            Look up all in Merlin
          </Button>
        </div>

        {lookupError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {lookupError}
          </div>
        )}

        {preview && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm space-y-2">
            <p className="text-slate-700">
              <span className="font-medium">Account:</span> {preview.account}
              {preview.customerid ? ` (customer ${preview.customerid})` : ""}
            </p>
            <ul className="space-y-1">
              {preview.lines.map((ln, i) => (
                <li key={i} className="flex items-start gap-1">
                  {ln.unallocated ? (
                    <span className="text-slate-600">
                      Line {i + 1}: no invoice ref — posts <strong>unallocated</strong>{" "}
                      (on-account).
                    </span>
                  ) : ln.error ? (
                    <span className="text-amber-700 flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4 shrink-0" /> Line {i + 1} (
                      {ln.invoiceRef}): {ln.error}
                    </span>
                  ) : ln.ambiguous ? (
                    <span className="text-amber-700 flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4 shrink-0" /> Line {i + 1}: invoice{" "}
                      {ln.invoiceRef} has multiple raising rows — can't post against it.
                    </span>
                  ) : ln.found && ln.sltranid ? (
                    <span className="text-slate-700">
                      Line {i + 1}: <strong>{ln.invoiceRef}</strong> · total{" "}
                      {gbp(ln.invoiceTotal)} · paid {gbp(ln.priorPaid)} · balance{" "}
                      {gbp(ln.balance)}
                    </span>
                  ) : (
                    <span className="text-amber-700 flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4 shrink-0" /> Line {i + 1}: invoice{" "}
                      {ln.invoiceRef} not found on this account.
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {result && (
          <div
            className={`rounded-lg border p-4 text-sm ${
              result.allPosted
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-amber-200 bg-amber-50 text-amber-800"
            }`}
          >
            <p className="font-semibold flex items-center gap-1">
              {result.allPosted ? (
                <CheckCircle2 className="w-4 h-4" />
              ) : (
                <AlertTriangle className="w-4 h-4" />
              )}
              {result.allPosted
                ? `All ${result.lines.length} line${result.lines.length === 1 ? "" : "s"} posted`
                : `${result.postedCount} of ${result.lines.length} lines posted — batch stopped`}
            </p>
            <p className="mt-1 text-xs">
              {result.account} · ref "{result.reference}" · total {gbp(result.totalAmount)}
            </p>
            <ul className="mt-2 space-y-1">
              {result.lines.map((ln) => (
                <li key={ln.index} className="flex items-start gap-1 text-xs">
                  {ln.posted && ln.code === "0" ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                  ) : ln.posted ? (
                    <AlertTriangle className="w-3.5 h-3.5 text-red-600 shrink-0 mt-0.5" />
                  ) : (
                    <MinusCircle className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                  )}
                  <span>
                    {gbp(ln.amount)} →{" "}
                    {ln.invoiceRef ? ln.invoiceRef : "unallocated"}
                    {ln.posted && ln.code === "0"
                      ? ` (sltranid ${ln.sltranid})`
                      : ln.posted
                        ? ` — rejected: code ${ln.code}${ln.message ? ` ${ln.message}` : ""}`
                        : ln.error
                          ? ` — ${ln.error}`
                          : " — not posted"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="pt-2 border-t border-slate-100">
          {!confirming ? (
            <Button
              type="button"
              onClick={() => {
                setIdempotencyKey((k) => k ?? makeIdempotencyKey());
                setConfirming(true);
              }}
              disabled={!canSubmit}
              className="w-full"
            >
              Review &amp; post to Merlin
            </Button>
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg border border-slate-300 bg-white p-3 text-sm text-slate-700">
                <p>
                  You are about to post {lineCount} line{lineCount === 1 ? "" : "s"} totalling{" "}
                  <strong>{gbp(totalAmount)}</strong> to <strong>{account.trim()}</strong>:
                </p>
                <ul className="mt-2 space-y-0.5 text-xs">
                  {parsedLines.map((l, i) => (
                    <li key={i}>
                      • {gbp(l.amount)} →{" "}
                      {l.invoiceRef ? `invoice ${l.invoiceRef}` : "unallocated (on-account)"}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-slate-500">
                  This cannot be undone except by posting opposite adjustments.
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setConfirming(false)}
                  disabled={postMutation.isPending}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => postMutation.mutate()}
                  disabled={postMutation.isPending}
                  className="flex-1"
                >
                  {postMutation.isPending && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  Confirm &amp; post
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CashReceiptsAdjustmentsPage() {
  const { isLoading, isRealAdmin } = useAuth();
  const [, setLocation] = useLocation();

  // Hard client-side gate (defence-in-depth on top of the route's gatedAdmin
  // and the server's requireAdmin). Non-admins are bounced to the home grid.
  useEffect(() => {
    if (isLoading) return;
    if (!isRealAdmin) setLocation("/");
  }, [isLoading, isRealAdmin, setLocation]);

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!isRealAdmin) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center">
          <ShieldAlert className="w-10 h-10 text-slate-400 mb-3" />
          <p className="text-slate-600 font-medium">Admin access required</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <ManualAdjustmentForm />
    </Layout>
  );
}
